import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubmissionsView = ({ onStatsUpdate }) => {
  const [newsletters, setNewsletters] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [resourceDownloads, setResourceDownloads] = useState([]);
  const { getAuthHeaders } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const [newsletterRes, contactRes, downloadRes] = await Promise.all([
        axios.get(`${API}/newsletter/subscriptions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contact/submissions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/resources/downloads`, { headers: getAuthHeaders() })
      ]);
      setNewsletters(newsletterRes.data.subscriptions || []);
      setContacts(contactRes.data.submissions || []);
      setResourceDownloads(downloadRes.data.downloads || []);
      onStatsUpdate(newsletterRes.data.subscriptions?.length || 0, contactRes.data.submissions?.length || 0);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch submissions", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Submissions</h2>

      <Tabs defaultValue="newsletter" className="space-y-4">
        <TabsList className="bg-gray-900">
          <TabsTrigger value="newsletter">Newsletter Subscribers ({newsletters.length})</TabsTrigger>
          <TabsTrigger value="contact">Contact Forms ({contacts.length})</TabsTrigger>
          <TabsTrigger value="downloads">Resource Downloads ({resourceDownloads.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="newsletter">
          <div className="space-y-3">
            {newsletters.map((sub, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-orange-400" />
                    <div>
                      <p className="text-white font-medium">{sub.email}</p>
                      <p className="text-gray-400 text-sm">
                        Subscribed: {new Date(sub.subscribed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="space-y-3">
            {contacts.map((contact, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="h-5 w-5 text-orange-400 mt-1" />
                    <div className="flex-1">
                      <CardTitle className="text-white text-lg">{contact.name}</CardTitle>
                      <CardDescription className="text-gray-400">{contact.email}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-semibold text-white mb-1">{contact.subject}</p>
                  <p className="text-gray-400 text-sm">{contact.message}</p>
                  <p className="text-gray-500 text-xs mt-2">
                    Submitted: {new Date(contact.submitted_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="downloads">
          <div className="space-y-3">
            {resourceDownloads.map((download, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-orange-400" />
                    <div className="flex-1">
                      <p className="text-white font-medium">{download.email}</p>
                      <p className="text-gray-400 text-sm">{download.resource_title}</p>
                      <p className="text-gray-500 text-xs">
                        Downloaded: {new Date(download.downloaded_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubmissionsView;
